'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
export const pe = {
  order: {
    units: 400,
    buyer: 'Department store',
    deposit: 0.5,
    terms: 'Net 60',
  },
  assumed: {
    pricePerUnit: 100,
    variableCostPerUnit: 55,
    orderCosts: 1200,
    allowance: 0.05,
  },
  sheets: [
    'Decision',
    'Inputs',
    'Economics',
    'Baseline',
    'Delayed',
    'Stress',
    'Checks',
  ],
  rows: [
    {
      metric: 'Net revenue',
      baseline: 38e3,
      stress: 34200,
      unit: 'usd',
    },
    {
      metric: 'Incremental contribution',
      baseline: 14800,
      stress: 7520,
      unit: 'usd',
    },
    {
      metric: 'Contribution margin',
      baseline: 0.389,
      stress: 0.22,
      unit: 'pct',
    },
    {
      metric: 'Peak funding gap',
      baseline: 3200,
      stress: 6680,
      unit: 'usd',
    },
    {
      metric: 'Deposit needed to avoid a gap',
      baseline: 0.58,
      stress: 0.667,
      unit: 'pct',
    },
  ],
  recommendation: 'Negotiate, then conditionally accept.',
  why: 'Profitable under the defaults, but a 50% deposit does not cover all costs before collection. Seek roughly 67% upfront.',
  checks: 'Formulas and cash reconciliations passed checks.',
};
export const me = {
  name: 'Wholesale Orders FW27',
  request:
    'Build us an app to track wholesale orders, deposits and stock by style.',
  tabs: ['Orders', 'Deposits', 'Stock by style', 'Accounts'],
  totals: [
    {
      label: 'Open order value',
      value: 212390,
      unit: 'usd',
    },
    {
      label: 'Deposits received',
      value: 31472,
      unit: 'usd',
    },
    {
      label: 'Deposits outstanding',
      value: 35772,
      unit: 'usd',
    },
    {
      label: 'Overdue deposits',
      value: 2,
      unit: 'count',
    },
  ],
  stages: ['Draft', 'Confirmed', 'In production', 'Ready to ship', 'Shipped'],
  orders: [
    {
      account: 'Harlow Department Store',
      id: 'FW27-0104',
      total: 42900,
      stage: 'Draft',
      deposit: 'due',
    },
    {
      account: 'Lark & Ledger',
      id: 'FW27-0107',
      total: 12100,
      stage: 'Draft',
      deposit: 'due',
    },
    {
      account: 'Northfield & Co',
      id: 'FW27-0101',
      total: 45460,
      stage: 'Confirmed',
      deposit: 'paid',
    },
    {
      account: 'Atelier Brun',
      id: 'FW27-0102',
      total: 39950,
      stage: 'Confirmed',
      deposit: 'overdue',
    },
    {
      account: 'Calder Supply',
      id: 'FW27-0103',
      total: 19260,
      stage: 'Confirmed',
      deposit: 'overdue',
    },
    {
      account: 'Tsuki Select',
      id: 'FW27-0105',
      total: 28360,
      stage: 'Confirmed',
      deposit: 'paid',
    },
    {
      account: 'The Woolhouse',
      id: 'FW27-0106',
      total: 24360,
      stage: 'In production',
      deposit: 'due',
    },
  ],
  stockFlags: [
    {
      style: 'Shearling Collar Coat',
      flag: 'Oversold',
      detail: '100 ordered against 90 inbound',
    },
    {
      style: 'Cable Knit',
      flag: 'Low',
      detail: 'Running low',
    },
    {
      style: 'Overcoat',
      flag: 'Low',
      detail: 'Running low',
    },
  ],
};
export const C = {
  asked: 'Your IMAI: forecast our cash for the next 13 weeks.',
  needsYou: 'I couldn\u2019t find your cash balance in the workspace.',
  weeks: 13,
  window: 'Oct 7, 2026 \u2013 Jan 5, 2027',
  inputs: {
    openingCash: 18e4,
    onlinePerWeek: 22e3,
    storePerWeek: 9e3,
    payrollBiweekly: 28e3,
    rentMonthly: 14e3,
    marketingPerWeek: 6e3,
    wholesaleDeposit: {
      week: 2,
      amount: 2e4,
    },
    productionPayments: [
      {
        week: 3,
        amount: 45e3,
      },
      {
        week: 9,
        amount: 45e3,
      },
    ],
  },
  low: {
    week: 9,
    weekEnding: 'Dec 8',
    base: 167e3,
    downside: 111200,
  },
  totals: {
    base: {
      receipts: 423e3,
      payments: 406e3,
      closing: 197e3,
    },
    downside: {
      receipts: 342400,
      payments: 406e3,
      closing: 116400,
    },
  },
  downsideRule:
    'Online and store sales 20% lower; the wholesale deposit arrives a week late.',
  closing: {
    base: [
      177e3, 222e3, 174e3, 185e3, 182e3, 207e3, 204e3, 215e3, 167e3, 192e3,
      189e3, 214e3, 197e3,
    ],
    downside: [
      170800, 189600, 155400, 160200, 151e3, 169800, 160600, 165400, 111200,
      13e4, 120800, 139600, 116400,
    ],
  },
  checks:
    'The weekly figures match an independent recalculation, and the workbook has no formula errors.',
};
export const xe = {
  sections: [
    'Business Performance',
    'Product & Production Economics',
    'Pricing & Promotions',
    'Cash & Working Capital',
    'Budgets & Scenarios',
    'Marketing & Channel Returns',
    'Financial Operations',
    'Funding & Capital Decisions',
  ],
  workflows: [
    {
      name: 'Check Collection Economics',
      does: 'Collection approval workbook linked to product versions.',
    },
    {
      name: 'Set a Margin-Based Price',
      does: 'Editable SKU and channel price calculator.',
    },
    {
      name: 'Evaluate a Wholesale Order',
      does: 'Order acceptance memo with editable economics and cash schedule.',
    },
    {
      name: 'Forecast Cash',
      does: 'Editable 13-week cash forecast and funding action brief.',
    },
    {
      name: 'Compare Supplier Terms',
      does: 'Supplier-term comparison and negotiation brief.',
    },
    {
      name: 'Test a Promotion',
      does: 'Promotion economics model and test brief.',
    },
    {
      name: 'Build a Budget',
      does: 'Monthly budget workbook and approval register.',
    },
    {
      name: 'Update the Forecast',
      does: 'Versioned rolling forecast with change bridge.',
    },
    {
      name: 'Stress-Test the Plan',
      does: 'Stress-testing workbook and contingency decision table.',
    },
    {
      name: 'Reconcile Sales & Payouts',
      does: 'Reconciliation workbook with an exception queue.',
    },
  ],
  retail: [
    'Plan Size Curves',
    'Allocate a New Collection',
    'Prevent Stockouts',
    'Plan Markdowns',
    'Explain a Sales Drop',
  ],
  method:
    'Editable workbooks with formulas, assumptions and units you can inspect.',
};
export const Se = {
  yourImai: {
    name: 'Your IMAI',
    greeting: 'Hey! I\u2019m your IMAI.',
    line: 'Text me anytime. I\u2019ll keep things moving, even when we\u2019re not talking, and check in with updates or questions.',
    facts: [
      'One personal chat per member, inside IMAI Studio.',
      'Quick questions answered in the chat; longer work runs in its own thread and reports back.',
      'Name it, and choose its colours and face.',
    ],
  },
  schedule: {
    line: 'Describe the task and when it should run. IMAI runs it for you.',
    presets: ['Once', 'Hourly', 'Daily', 'Weekdays', 'Weekly', 'Monthly'],
    examples: [
      'Weekly trend digest',
      'Competitor watch',
      'Daily project recap',
    ],
  },
  apps: {
    line: 'Dashboards, trackers, boards, review queues and planners that keep their data and refresh on a schedule.',
  },
};
